import { AxiosResponse } from 'axios';
import { makeAutoObservable } from 'mobx';
import qs from 'qs';

import { axiosClient } from '../config/axios-client';
import { AvailableBotForSitResponse } from '../model/availablebot/AvailableBotForSitResponse';
import { AvailableBotPage } from '../model/availablebot/AvailableBotPage';
import { AvailableBotResponse } from '../model/availablebot/AvailableBotResponse';
import {
  AvailableBotSearchFilterOption,
  AvailableBotSearchFilterRequest,
} from '../model/availablebot/AvailableBotSearchFilterRequest';
import { PrStats } from '../model/availablebot/PrStats';
import { GameType } from '../model/enumeration/GameType.enum';
import { PokerRoom } from '../model/enumeration/PokerRoom';
import { TableUnion } from '../model/TableUnion';
import sessionStorageStore from './SessionStorageStore';

const BASE_API_PATH = '/bots/available';

const DEFAULT_SEARCH_FILTER: AvailableBotSearchFilterRequest = {
  clubs: [],
  leagues: [],
  superLeagues: [],
  gameTypes: [],
  searchName: '',
};

class AvailableBotStore {
  public searchFilter: AvailableBotSearchFilterRequest = DEFAULT_SEARCH_FILTER;

  public constructor() {
    makeAutoObservable(this);
    this.searchFilter =
      sessionStorageStore.getAvailableBotsSearchFilter() ??
      DEFAULT_SEARCH_FILTER;
  }

  public getAvailableBotsForSit = (
    tableId: string,
    room: PokerRoom,
    gameType: GameType,
    limit: number,
    union: TableUnion,
  ): Promise<AvailableBotForSitResponse[]> =>
    axiosClient
      .get<AvailableBotForSitResponse[]>(`${BASE_API_PATH}/sit`, {
        params: { ...union, tableId, room, gameType, limit },
      })
      .then(response => response.data);

  public getAvailableBotPage = (
    page: number,
    size: number,
    onlyManualSit: boolean,
  ): Promise<AvailableBotPage> =>
    axiosClient
      .get<AvailableBotPage>(BASE_API_PATH, {
        params: { ...this.searchFilter, page, size, onlyManualSit },
        paramsSerializer: params => {
          return qs.stringify(params, { arrayFormat: 'repeat' });
        },
      })
      .then(response => response.data);

  public getAvailableBotsWithClubs = (
    clubs: string[],
  ): Promise<AvailableBotResponse[]> =>
    axiosClient
      .get<AvailableBotResponse[]>(`${BASE_API_PATH}/with-clubs`, {
        params: { clubs },
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: 'repeat' }),
      })
      .then(response => response.data);

  public getAvailableBot = (botId: string): Promise<AvailableBotResponse> =>
    axiosClient
      .get<AvailableBotResponse>(`${BASE_API_PATH}/${botId}`)
      .then(response => response.data);

  public updatePrStats = (
    botId: string,
    room: PokerRoom,
    prStats: PrStats,
  ): Promise<AxiosResponse<void>> =>
    axiosClient.put(`${BASE_API_PATH}/${botId}/prstats`, prStats, {
      params: { room },
    });

  public updateClub = (
    botId: string,
    room: PokerRoom,
    club: string,
  ): Promise<AxiosResponse<void>> =>
    axiosClient.put(`${BASE_API_PATH}/${botId}/club`, null, {
      params: { room, club },
    });

  public resetRestrictions = (
    botId: string,
    room: PokerRoom,
  ): Promise<AxiosResponse<void>> =>
    axiosClient.put(`${BASE_API_PATH}/${botId}/reset-restrictions`, null, {
      params: { room },
    });

  public joinTableByLink = (
    botId: string,
    room: PokerRoom,
    nick: string,
    joinLink: string,
    buyIn: number,
  ): Promise<AxiosResponse<void>> =>
    axiosClient.put(
      `${BASE_API_PATH}/${botId}/force-join-table-by-link`,
      null,
      {
        params: { room, nick, joinLink, buyIn },
      },
    );

  public updateFilterOptions = (
    options: AvailableBotSearchFilterOption,
  ): void => {
    this.searchFilter = { ...this.searchFilter, ...options };
    sessionStorageStore.setAvailableBotsSearchFilter(this.searchFilter);
  };

  public resetFilter = (): void => {
    this.searchFilter = DEFAULT_SEARCH_FILTER;
    sessionStorageStore.setAvailableBotsSearchFilter(this.searchFilter);
  };
}

const availableBotStore = new AvailableBotStore();
export default availableBotStore;
